import { FINANCE_AUTOMATIC_INCOME_KEYS } from './financeEntries';

/**
 * Task 6.3 (design 2b) — Performance → Overview.
 *
 * `GET organizations/:organizationId/performance/overview?from&to`. The route
 * admits a holder of EITHER `stats_org_read` or `finance_stats_read`, and the
 * answer carries only the block(s) the caller may read: `work` for
 * `stats_org_read`, `money` for `finance_stats_read` (never for a trainer),
 * both for a platform user. A block the caller may not read is ABSENT — not
 * empty, not zero.
 *
 * Definitions are the decided ones of Task 6.2
 * (`packages/@mfw-backend/src/stats/performance-definitions.ts`); every day is
 * a civil day of the organization's zone (`tz`).
 */

/** An automatic income key of Profits & Expenses (Task 5.2) — the same keys,
 *  the same ledger rows, so the two screens reconcile. */
export type PerformanceRevenueCategoryKey =
  (typeof FINANCE_AUTOMATIC_INCOME_KEYS)[number];

export interface PerformanceVisitsDay {
  /** civil day, 'yyyy-MM-dd' */
  day: string;
  count: number;
}

/** PO 08.10. (1.5d) — one booking session at an occupancy extreme. */
export interface PerformanceTodaySession {
  eventId: string;
  title: string;
  /** ISO instant */
  startsAt: string;
  /** seats held (BOOKED / ATTENDED / NO_SHOW) */
  held: number;
  capacity: number;
  /** held ÷ capacity, 0..1 */
  occupancy: number;
}

/**
 * PO 08.10. (1.5b–d) — the organization's TODAY (its civil day, whatever the
 * range), read by the same rules as the staff "Day overview"
 * (`staff-home/arrival-day.ts`). An employed trainer sees their own clients
 * only: no guests, and only the day passes sold with them.
 */
export interface PerformanceOverviewToday {
  /** the organization's today, 'yyyy-MM-dd' */
  day: string;
  arrivals: {
    /** booking + standalone */
    total: number;
    /** seats on today's sessions — members (via a linked check-in or an
     *  ATTENDED seat) and guests (walk-in) */
    booking: number;
    /** desk check-ins + guest day passes */
    standalone: number;
    /** of `standalone`: guest day passes */
    dayPass: number;
    /** of `booking`: guests without an account */
    walkIn: number;
  };
  workouts: {
    /** "Done workout" marked WITH_TRAINER today — never an arrival
     *  (PO 05.10.) */
    withTrainer: number;
    /** attended seats on today's sessions — the same number as
     *  `arrivals.booking` */
    booking: number;
  };
  occupancy: {
    /** mean per-session held ÷ capacity of today's sessions; `null` when
     *  none ran */
    avg: null | number;
    highest: null | PerformanceTodaySession;
    lowest: null | PerformanceTodaySession;
  };
}

/**
 * PO 08.10.2026 (1.2, Q9) — active members on the last day of the period that
 * has happened, and on the same civil day one month earlier. "Active" is THE
 * predicate (`isActiveMemberOn`): a live qualifying membership covers the
 * day, paused included, day passes never, a disconnected client not.
 */
export interface PerformanceActiveMembers {
  /** min(`to`, today), 'yyyy-MM-dd' */
  day: string;
  count: number;
  /** `day` minus one calendar month (clamped to the month's last day) */
  previousDay: string;
  previousCount: number;
}

export interface PerformanceOverviewWork {
  /** PO 08.10.2026 (1.2) */
  activeMembers: PerformanceActiveMembers;
  /** Non-voided `MembershipCheckIns` on the organization's today. */
  checkInsToday: number;
  /**
   * Non-voided check-ins in `[from, min(to, today)]` ÷ the number of those
   * days (days after today have not happened yet). `null` when the range
   * starts after today.
   */
  checkInsAvgPerDay: null | number;
  /** `Schedules` with `status = COMPLETED` whose civil day is in the range. */
  workoutsDone: number;
  /** `Schedules` not CANCELLED whose civil day is in the range. */
  workoutsAssigned: number;
  /**
   * Mean per-session occupancy (held seats ÷ capacity, 0..1) of the booking
   * sessions starting in the range — `BookingStatsService.getOccupancy`.
   * `null` when no session ran.
   */
  bookingOccupancy: null | number;
  /** One row per civil day of `[from, min(to, today)]`, zeros included —
   *  the same check-ins as `checkInsAvgPerDay`. */
  visitsByDay: PerformanceVisitsDay[];
  /** PO 08.10. — the organization's today, independent of `from`/`to`. */
  today: PerformanceOverviewToday;
}

export interface PerformanceRevenueMonth {
  /** 'yyyy-MM' in the organization's zone */
  month: string;
  /**
   * Collected gross − refunds − disputes per automatic income key, BIGINT
   * minor-unit strings (can be negative in a month whose only movement was a
   * refund). `platform_billing` is never income; marketplace is on the net
   * result's collected basis (ruling V2-3). This is ALL the money, day passes
   * included — the same figures as Profits & Expenses (Task 5.2).
   *
   * `null` when the month has no ledger movement at all in this currency
   * ("—"); a month whose movements net to zero is `"0"` (ruling V2-19).
   */
  byCategory: Record<PerformanceRevenueCategoryKey, string> | null;
}

/**
 * Every figure is in ONE currency; nothing is ever summed across them.
 *
 * Ruling V2-18 — the membership money of the Overview (`mrrRealizedMinor`,
 * the LTV and ARPC numerators, `due*`) EXCLUDES day passes, so it is read on
 * the same qualifying population as the client counts it is divided by. A
 * charge is a day pass by its package (`PaymentTransactions.membershipId` →
 * `Memberships`; its assignment when the charge names no package), a refund
 * or dispute by its parent charge, a due cycle by its assignment snapshot.
 * `revenueByMonth` alone keeps all money (Profits & Expenses basis).
 */
export interface PerformanceOverviewCurrencyMoney {
  currency: string;
  /**
   * Membership money (charges − refunds − disputes, day passes excluded)
   * collected in the organization-zone month of `to`, from its first day
   * through `to` (month to date).
   */
  mrrRealizedMinor: string;
  /**
   * Σ `finalPrice` of the qualifying assignments live on `to`, each
   * normalized to one month (MONTHS n → ÷n, WEEKS n → ×30/(7n), DAYS n →
   * ×30/n), paused ones included; rounded half-up once, after the sum.
   */
  mrrProjectedMinor: string;
  /** 12 × `mrrProjectedMinor`. */
  arrProjectedMinor: string;
  /**
   * Collected membership money (day passes excluded) through `to` of the
   * clients who had a qualifying assignment in this currency starting on or
   * before `to`, ÷ the number of those clients. `null` when there are none.
   */
  ltvMinor: null | string;
  /**
   * `mrrRealizedMinor` ÷ the clients with a qualifying assignment in this
   * currency live on at least one day of that month (through `to`). `null`
   * when there are none.
   */
  arpcMinor: null | string;
  /** Live, already-started qualifying cycles (no day passes) whose payment
   *  state today is UNPAID or PARTIAL
   *  (`MembershipFinanceService.cyclePaymentStates`). */
  dueCount: number;
  /** Σ what is still due on those cycles. */
  dueExpectedMinor: string;
  /** The 12 organization-zone months ending with the month of `to`, oldest
   *  first; the last month runs through `to`. */
  revenueByMonth: PerformanceRevenueMonth[];
}

export interface PerformanceOverviewMoney {
  /**
   * AAM — average active months: the mean, over the organization's clients
   * that had a qualifying assignment starting on or before `to`, of the days
   * through `to` covered by one (overlaps once, gaps excluded) ÷ 30.44, one
   * decimal. Currency-free, so it is stated once. `null` when there are no
   * such clients.
   */
  aamMonths: null | number;
  /** The organization's own currency first, then alphabetical. */
  perCurrency: PerformanceOverviewCurrencyMoney[];
}

export interface PerformanceOverviewResponse {
  /** inclusive civil day */
  from: string;
  /** inclusive civil day */
  to: string;
  /** the organization's zone every day above is cut in */
  tz: string;
  /** present only for `stats_org_read` holders (and platform users) */
  work?: PerformanceOverviewWork;
  /** present only for `finance_stats_read` holders who are not trainers
   *  (and platform users) */
  money?: PerformanceOverviewMoney;
}

// ── Task 6.6 (design 2e) — Performance → Retention & Churn ──────────────────

/**
 * `GET organizations/:organizationId/performance/retention?from&to`
 * (`stats_org_read`). No money anywhere in this answer. Definitions: Task 6.2
 * (`packages/@mfw-backend/src/stats/performance-definitions.ts`) with ruling
 * V2-4 — "active on day d" is read off a qualifying assignment's DATES
 * (`[startDate, endDate)`, status ACTIVE or FROZEN), "paused on d" off the
 * `MembershipPauses` history. Day passes never count.
 *
 * Every client is counted ONCE per figure: a client with two signals is one
 * churn-risk row and one distribution bucket; a client who churns (lapses
 * and is then disconnected) is one churned client.
 *
 * Days: every day is a civil day of the organization's zone. `asOf` =
 * min(`to`, today) is the day the range-end figures are read on (days after
 * today have not happened); the churn-risk block is always a "today" view.
 * Percentages are 0..100 with one decimal, `null` when the denominator is 0.
 *
 * A range that starts after today (`from` > `asOf`) has not happened: its
 * flow counts and percentages are `null`, never 0 (ruling V2-25); the
 * snapshot counts (`inactiveClients`, `formerClients`, `pausedNow`) are
 * still read on `asOf` / today.
 */

/** The churn-risk signals (Task 6.2), in severity order. */
export type PerformanceRiskSignalName =
  | 'payment_late'
  | 'no_visit'
  | 'visit_drop'
  | 'expiring';

export type PerformanceRiskSeverity = 'high' | 'medium' | 'low';

/** One signal with the facts behind it — numbers and days only, never money,
 *  so the screen phrases it in the reader's language. */
export type PerformanceRiskSignal =
  | {
      signal: 'payment_late';
      /** the oldest unpaid (UNPAID/PARTIAL) cycle of the current stay:
       *  today − its start day; > 7 */
      detail: { daysLate: number };
    }
  | {
      signal: 'no_visit';
      /** today − the last visit (check-in or ATTENDED booking), or − the
       *  start of the current stay when there was no visit in it; ≥ 14 */
      detail: { daysSinceVisit: number; lastVisit: null | string };
    }
  | {
      signal: 'visit_drop';
      /** visits in the last 4 weeks (`recent`, today included) vs the 4
       *  weeks before (`previous`); `dropPercent` ≥ 50, rounded */
      detail: { dropPercent: number; previous: number; recent: number };
    }
  | {
      signal: 'expiring';
      /**
       * The day the client's stretch of stays ends (the expiry day, the
       * first day NOT covered) with no renewal scheduled. `daysLeft` =
       * `endDate` − today: 1..7 while still covered; NEGATIVE once the stay
       * has already ended ("ended N days ago") — a client in the post-stay
       * gap, fewer than 30 days after it, stays in the list until they move
       * to `inactive` (ruling V2-25).
       */
      detail: { daysLeft: number; endDate: string };
    };

export interface PerformanceRetentionSummary {
  /** active at `from` AND (active or paused) at `asOf` ÷ active at `from` */
  retentionPct: null | number;
  /** churned in [from, asOf] ÷ active at `from` */
  churnPct: null | number;
  /** first-ever qualifying membership started in [from, asOf]; `null` for
   *  a range that starts after today */
  newClients: null | number;
  /** started a qualifying membership in [from, asOf] after having lapsed
   *  (inactive) or been disconnected (former); `null` for a future range */
  returnedClients: null | number;
  /** became inactive (last end + 30 days) or was disconnected while not yet
   *  counted, in [from, asOf] — each client once; `null` for a future range */
  churnedClients: null | number;
  /** `inactive.length` — connected, no coverage on `asOf`, last end ≥ 30
   *  days before it */
  inactiveClients: number;
  /** disconnected on or before `asOf` and not connected again */
  formerClients: number;
  /** paused today (`MembershipPauses`) — the same count as
   *  `riskDistribution.paused` */
  pausedNow: number;
  /** newClients + returnedClients − churnedClients; `null` for a future
   *  range */
  netGrowth: null | number;
}

/** One organization-zone month. Every count is `null` for a month that
 *  starts after `asOf` (it has not happened yet). */
export interface PerformanceRetentionMonth {
  /** 'yyyy-MM' */
  month: string;
  new: null | number;
  returned: null | number;
  churned: null | number;
  /** paused on the month's last day (or on `asOf`, for the month of it) */
  pausedAtMonthEnd: null | number;
}

export interface PerformanceInactiveClient {
  clientId: string;
  name: string;
  /** the expiry day of the last qualifying membership (its `endDate`) */
  lastMembershipEnd: string;
  /** `asOf` − `lastMembershipEnd`; always ≥ 30 */
  inactiveDays: number;
}

export interface PerformanceChurnRiskRow {
  clientId: string;
  name: string;
  /** the package of the qualifying membership covering today — or, in the
   *  post-stay gap, of the one that ended last */
  membershipName: null | string;
  /** every signal that fired, highest severity first */
  signals: PerformanceRiskSignal[];
  /** the highest severity among `signals` */
  severity: PerformanceRiskSeverity;
}

/** Distinct clients: each churn-risk row once by its severity, plus the
 *  paused clients (who get no signal). */
export interface PerformanceRiskDistribution {
  high: number;
  medium: number;
  low: number;
  paused: number;
}

export interface PerformanceCohortRow {
  /** 'yyyy-MM' — the month of the clients' FIRST qualifying membership */
  month: string;
  clients: number;
  /**
   * `COHORT_MONTHS` entries: [k − 1] = % of the cohort active on the last
   * day of the k-th following month; `null` while that day is after
   * `asOf` (or for an empty cohort).
   */
  retained: (null | number)[];
}

export interface PerformanceRetentionByMembership {
  membershipId: string;
  name: string;
  /** clients active at `from` whose covering membership is this package —
   *  the denominator */
  clients: number;
  /** the summary's retention, on those clients only */
  retentionPct: null | number;
}

export interface PerformanceRetentionResponse {
  /** inclusive civil day */
  from: string;
  /** inclusive civil day */
  to: string;
  /** min(`to`, today) — the day range-end figures are read on */
  asOf: string;
  /** the organization's today — the churn-risk block's day */
  today: string;
  /** the organization's zone */
  tz: string;
  summary: PerformanceRetentionSummary;
  /** the 12 organization-zone months ending with the month of `to`, oldest
   *  first */
  monthly: PerformanceRetentionMonth[];
  /** most inactive days first */
  inactive: PerformanceInactiveClient[];
  /** high first, then medium, then low; within a severity by name */
  churnRisk: PerformanceChurnRiskRow[];
  riskDistribution: PerformanceRiskDistribution;
  /** one row per month of `monthly`, oldest first */
  cohorts: PerformanceCohortRow[];
  /** by package name */
  byMembershipType: PerformanceRetentionByMembership[];
}
// ── PO 08.10. — Performance → Revenue KPIs ──────────────────────────────────

/** One organization-zone month of the revenue KPIs. */
export interface PerformanceRevenueMonthKpi {
  /** 'yyyy-MM' */
  month: string;
  /** collected gross − refunds − disputes (all money); `null` with no
   *  ledger movement in the month ("—") */
  collectedMinor: null | string;
  /** MRR projected (Overview rule) on the month's last day — or on `asOf`
   *  for the month of it; `null` for a month starting after `asOf` */
  mrrProjectedMinor: null | string;
  /** 12 × `mrrProjectedMinor` */
  arrProjectedMinor: null | string;
}

export interface PerformanceRevenueCurrencyKpis {
  currency: string;
  /** qualifying ledger movements exist in [from, asOf]; false means the
   *  zero-valued money placeholders below must be displayed as dashes */
  hasRevenue: boolean;
  /** collected in [from, asOf], all money */
  totalMinor: string;
  /** `totalMinor` by automatic income key — the Profits & Expenses split */
  byCategory: Record<PerformanceRevenueCategoryKey, string>;
  /** clients whose collected money in the range is > 0 */
  payingClients: number;
  /** Σ those clients' collected ÷ `payingClients`; guest money (no client)
   *  is in `totalMinor` only */
  avgPerClientMinor: null | string;
  /** `totalMinor` ÷ trainers ÷ (days ÷ 30.44) */
  avgPerTrainerMonthlyMinor: null | string;
  /** `avgPerTrainerMonthlyMinor` ÷ (trainerClientPairs ÷ trainers) =
   *  `totalMinor` ÷ trainerClientPairs ÷ (days ÷ 30.44); null without trainers */
  avgPerTrainerClientMonthlyMinor: null | string;
  /** PO Q7 — `byCategory.booking_sessions` ÷ `sessionsHeld`; `null` when
   *  no session was held */
  avgPerSessionMinor: null | string;
  /** PO Q7 — `totalMinor` ÷ `visits` (visits carry no currency); `null`
   *  when there were no visits */
  avgPerVisitMinor: null | string;
  /** the 12 organization-zone months ending with the month of `to` */
  months: PerformanceRevenueMonthKpi[];
}

/**
 * `GET organizations/:organizationId/performance/revenue?from&to`
 * (`finance_stats_read`; never for an employed trainer). Every money figure
 * is in ONE currency, never summed across them.
 */
export interface PerformanceRevenueResponse {
  from: string;
  to: string;
  /** min(`to`, today) */
  asOf: string;
  tz: string;
  /** civil days of [from, asOf]; 0 for a range that starts after today */
  days: number;
  /** ACTIVE trainer memberships of the organization now */
  trainers: number;
  /** distinct connected clients assigned to an ACTIVE trainer now */
  assignedClients: number;
  /** distinct (trainer, client) pairs now; one shared client can add two */
  trainerClientPairs: number;
  /** booking sessions held in [from, asOf] (not deleted, not cancelled,
   *  already started) */
  sessionsHeld: number;
  /** visits in [from, asOf]: `visitEventsSql` (check-ins + attended seats,
   *  guests' seats included) + guest day passes */
  visits: number;
  /** the organization's currency first, then alphabetical */
  perCurrency: PerformanceRevenueCurrencyKpis[];
}

// ── PO 08.10. — Performance → Memberships, every membership ────────────────

/** Qualifying assignments by status on `asOf` (each counted once). */
export interface PerformanceMembershipStatusCounts {
  active: number;
  paused: number;
  /** waiting to start (future start, or PENDING / waiting for a card) */
  startsLater: number;
  /** ended naturally in [from, asOf] and not renewed (a live successor
   *  continues a renewed row — Ruling A, as in Retention) */
  ended: number;
  /** cancelled in [from, asOf] */
  cancelled: number;
  /** of active + paused: the current cycle is unpaid or partly paid; `null`
   *  unless `asOf` is today */
  due: null | number;
}

/** `GET organizations/:organizationId/performance/memberships?from&to`
 *  (`stats_org_read` OR `finance_stats_read`). No money. */
/** PO 08.10.2026 (3.1) — sales per civil day of the period. */
export interface PerformanceMembershipsSoldDay {
  day: string;
  memberships: number;
  dayTickets: number;
}

/**
 * PO 08.10.2026 (3.1) — memberships and day tickets SOLD in [from, asOf]: an
 * assignment counts on the organization-zone day it was created (a renewal
 * is a sale); deleted, refunded and never-started (EXPIRED) ones do not.
 * Day tickets = day-pass assignments + guest day tickets not voided.
 */
export interface PerformanceMembershipsSold {
  memberships: number;
  dayTickets: number;
  /** every day of [from, asOf], zeros included; empty when from > asOf */
  byDay: PerformanceMembershipsSoldDay[];
}

export interface PerformanceMembershipsResponse {
  from: string;
  to: string;
  asOf: string;
  today: string;
  tz: string;
  byStatus: PerformanceMembershipStatusCounts;
  /** PO 08.10.2026 (3.1) */
  sold: PerformanceMembershipsSold;
}

/**
 * PO 08.10.2026 (items 2.1–2.3) — Performance → Clients & groups.
 *
 * `GET organizations/:organizationId/performance/clients`. The four lists
 * are TODAY views (who is active / due / inactive-or-former now); every
 * ranked metric reads the range `[from, to]`. Money
 * (`PerformanceClientRow.money`, `PerformanceGroupRow.membershipRevenueMinor`)
 * is present only when `moneyVisible` — never for an employed trainer — and
 * is in ONE currency (`currency`); currencies are never summed.
 */
export const PERFORMANCE_CLIENT_LISTS = [
  'active',
  'due',
  'lapsed',
  'groups',
] as const;
export type PerformanceClientList = (typeof PERFORMANCE_CLIENT_LISTS)[number];

/**
 * PO 08.10.2026, question 6 (approved) — how a client trains in the period.
 * A client can be of two types; a filter of several types is ANY:
 *  - `withTrainer` — a membership of theirs in the period is a TRAINER
 *    package (`Memberships.includesTrainer`);
 *  - `booking` — attended at least one booking session in the period;
 *  - `membershipOnly` — neither of the two.
 */
export const PERFORMANCE_TRAINING_TYPES = [
  'withTrainer',
  'booking',
  'membershipOnly',
] as const;
export type PerformanceTrainingType =
  (typeof PERFORMANCE_TRAINING_TYPES)[number];

export const PERFORMANCE_CLIENT_WORK_SORTS = [
  'checkIns',
  'renewals',
  'pausedDays',
] as const;
export const PERFORMANCE_CLIENT_MONEY_SORTS = [
  'payments',
  'totalPaid',
  'avgPayment',
  'receptionSpend',
] as const;
export const PERFORMANCE_CLIENT_SORTS = [
  ...PERFORMANCE_CLIENT_WORK_SORTS,
  ...PERFORMANCE_CLIENT_MONEY_SORTS,
] as const;
export type PerformanceClientSort = (typeof PERFORMANCE_CLIENT_SORTS)[number];

/** A ranking that reads money — refused to a caller who may not read it. */
export const isPerformanceClientMoneySort = (sort: PerformanceClientSort) =>
  (PERFORMANCE_CLIENT_MONEY_SORTS as readonly string[]).includes(sort);

export type PerformanceSortOrder = 'asc' | 'desc';

/** One client's money in the response `currency`. */
export interface PerformanceClientMoney {
  /** Collected charges in the range whose own net (after every refund or
   *  dispute, whenever posted) stayed above zero — a voided sale is not a
   *  payment. */
  payments: number;
  /** Collected − refunds − disputes posted in the range; `null` = no
   *  ledger movement in this currency ("—"). */
  totalPaidMinor: null | string;
  /** `totalPaidMinor ÷ payments`, half-up; `null` when `payments` is 0. */
  avgPaymentMinor: null | string;
  /** The reception (desk sale) part of `totalPaidMinor`; `null` = none. */
  receptionSpendMinor: null | string;
  /** Still due TODAY on the client's current cycle(s); `null` = nothing
   *  due in this currency. */
  dueMinor: null | string;
}

export interface PerformanceClientRow {
  clientId: string;
  name: string;
  /** Visits in the range: non-voided check-ins + ATTENDED bookings, each
   *  visit once (`visitEventsSql`). */
  checkIns: number;
  /** Qualifying memberships that START in `[from, min(to, today)]` and are
   *  not the client's first one in the organization. */
  renewals: number;
  /** DISTINCT days of `[from, min(to, today)]` covered by a non-cancelled
   *  pause window — overlapping windows count a day once. */
  pausedDays: number;
  /** `lapsed` list: still connected, days since the last membership ended
   *  (30 and up); `null` otherwise. */
  inactiveDays: null | number;
  /** `lapsed` list: civil day of the disconnect; `null` while connected. */
  formerSince: null | string;
  /** Absent when `moneyVisible` is false or there is no `currency`. */
  money?: PerformanceClientMoney;
}

export interface PerformanceGroupRow {
  groupId: string;
  name: string;
  /** Active members that pass the filters (an employed trainer: only their
   *  own clients). */
  members: number;
  /** Σ members' visits in the range. */
  checkIns: number;
  /** The package most members held during the range (distinct clients). */
  topMembership: null | {
    membershipId: string;
    name: string;
    clients: number;
  };
  /** The booking session type (event template title) members attended
   *  most in the range; `title: null` = sessions without a type. */
  topSessionType: null | { title: null | string; visits: number };
  /** Σ members' membership payments in `currency` (refunds subtracted);
   *  `null` = none. Absent without money. A client in two groups counts in
   *  both. */
  membershipRevenueMinor?: null | string;
}

export interface PerformanceClientsCounts {
  active: number;
  /** `null` when the caller may not read money: who is due is a ledger
   *  fact (payment state), never computed for such a caller */
  due: null | number;
  lapsed: number;
  groups: number;
}

export interface PerformanceClientsResponse {
  from: string;
  to: string;
  /** the organization's civil today — the day the four lists are read on */
  today: string;
  tz: string;
  list: PerformanceClientList;
  sort: PerformanceClientSort;
  order: PerformanceSortOrder;
  counts: PerformanceClientsCounts;
  /** One page of the ranked client list; empty for `groups`. */
  rows: PerformanceClientRow[];
  /** All rows of the list before paging (groups: the number of groups). */
  total: number;
  /** `groups` list only; empty otherwise. */
  groups: PerformanceGroupRow[];
  /** The caller may read money (owner / finance reader / platform user). */
  moneyVisible: boolean;
  /** Currency of every money figure; `null` without money. */
  currency: null | string;
  /** Currencies with money in the range or due today (page filter). */
  currencies: string[];
  /** The caller is narrowed to their own clients (employed trainer). */
  trainerScoped: boolean;
}
// ── PO 08.10.2026 — payment methods (3.2) and "by payment method" (Q4) ────

/** Q3: cash → Cash, card → Card, transfer → Invoice, the rest → Other. */
export const PERFORMANCE_PAYMENT_METHOD_BUCKETS = [
  'cash',
  'card',
  'invoice',
  'other',
] as const;
export type PerformancePaymentMethodBucket =
  (typeof PERFORMANCE_PAYMENT_METHOD_BUCKETS)[number];

export interface PerformancePaymentMethodsCurrency {
  currency: string;
  /** 3.2 — membership money collected in the period, per bucket (4 rows, in
   *  `PERFORMANCE_PAYMENT_METHOD_BUCKETS` order); BIGINT minor strings */
  collected: {
    bucket: PerformancePaymentMethodBucket;
    collectedMinor: string;
  }[];
  /** Σ `collected` — the 100 % the shares are read against */
  collectedTotalMinor: string;
  /** Q4 — MRR projected on `asOf` by the method of each live membership's
   *  latest payment; `notPaid` = never paid (5 rows, `notPaid` last) */
  mrrProjected: {
    bucket: PerformancePaymentMethodBucket | 'notPaid';
    memberships: number;
    minor: string;
  }[];
  /** Q4 — LTV per method (4 rows); `minor` null = no client paid that way */
  ltv: {
    bucket: PerformancePaymentMethodBucket;
    clients: number;
    minor: null | string;
  }[];
}

export interface PerformancePaymentMethodsResponse {
  from: string;
  to: string;
  /** min(`to`, today) */
  asOf: string;
  tz: string;
  /** the organization's own currency first, then alphabetical */
  perCurrency: PerformancePaymentMethodsCurrency[];
}
// ── PO 08.10.2026 — trainer work and session ranking (1.7 / 5.2 / Q5) ─────

/** One currency of a trainer's money; BIGINT minor strings. */
export interface PerformanceTrainerMoney {
  currency: string;
  /** every scheduled workout at its package's visit value */
  expectedMinor: string;
  /** workouts completed WITH the trainer */
  completedMinor: string;
  /** missed workouts — charged (PO Q5) */
  missedMinor: string;
  /** completed + missed */
  earnedMinor: string;
}

export interface PerformanceTrainerWorkRow {
  trainerId: string;
  name: string;
  /** non-cancelled scheduled workouts */
  scheduled: number;
  /** marked "with trainer" (scheduled or not) */
  completedWithTrainer: number;
  /** scheduled and MISSED — charged (Q5) */
  missed: number;
  /** scheduled, COMPLETED without the "with trainer" mark — not the
   *  trainer's, never money */
  bonus: number;
  /** completedWithTrainer + missed */
  work: number;
  /** empty when the caller may not read money */
  money: PerformanceTrainerMoney[];
}

export interface PerformanceTrainerWorkResponse {
  from: string;
  to: string;
  asOf: string;
  tz: string;
  withMoney: boolean;
  /** most work first */
  rows: PerformanceTrainerWorkRow[];
}

export interface PerformanceSessionRankRow {
  eventId: string;
  title: string;
  /** ISO instant */
  startsAt: string;
  /** held seats (BOOKED / ATTENDED / NO_SHOW) */
  attendees: number;
  capacity: number;
  /** Σ seat unit values per currency; empty without money */
  money: { currency: string; minor: string }[];
}

export interface PerformanceLeaderboardsResponse {
  /** "this day" = min(`to`, today) */
  day: string;
  from: string;
  to: string;
  tz: string;
  withMoney: boolean;
  trainers: {
    thisDay: PerformanceTrainerWorkRow[];
    toDate: PerformanceTrainerWorkRow[];
  };
  /** most attendees first */
  sessions: {
    thisDay: PerformanceSessionRankRow[];
    toDate: PerformanceSessionRankRow[];
  };
}
// ── PO 08.10.2026 — daily revenue, collected and earned (1.5e / 1.6 / Q10) ──

export const PERFORMANCE_DAILY_REVENUE_KINDS = [
  'memberships',
  'personal_trainings',
  'day_tickets',
  'booking_sessions',
  'reception',
  'services',
  'other',
] as const;
export type PerformanceDailyRevenueKind =
  (typeof PERFORMANCE_DAILY_REVENUE_KINDS)[number];

export interface PerformanceDailyRevenueDay {
  /** 'yyyy-MM-dd', organization zone */
  day: string;
  /** money that came in that day, refunds that day deducted; BIGINT minor
   *  strings, every kind present (can be negative) */
  collected: Record<PerformanceDailyRevenueKind, string>;
  /** what that day's visits, seats, day tickets and desk sales are worth,
   *  paid or not (PO Q10); `other` is always "0" */
  earned: Record<PerformanceDailyRevenueKind, string>;
}

export interface PerformanceDailyRevenueCurrency {
  currency: string;
  /** every day of [from, asOf], zeros included */
  days: PerformanceDailyRevenueDay[];
}

export interface PerformanceDailyRevenueResponse {
  from: string;
  to: string;
  /** min(`to`, today) */
  asOf: string;
  tz: string;
  /** the organization's own currency first, then alphabetical */
  perCurrency: PerformanceDailyRevenueCurrency[];
}
